import {AfterViewInit, Component, inject, Input, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {Store} from "@ngrx/store";
import {CopyFundReportAndValuationsActions, FundReportActions} from "../../store/fund.actions";
import {selectFundReportDeleteError, selectSelectedFundReportsForFund} from "../../store/fund-report/fund-report.selectors";
import {DateUtil} from "../../../shared/utils/date-util";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {selectFundIsArchived} from "../../store/fund/fund.selectors";
import {BehaviorSubject, map, Subscription} from "rxjs";
import {filter, take} from "rxjs/operators";
import {AddFundReportDialogComponent} from "../add-fund-report-dialog/add-fund-report-dialog.component";
import {FundCopyWorkflowStepperComponent} from "../fund-copy-workflow-stepper/fund-copy-workflow-stepper.component";
import {CopyConfig, FundReportCopyConfig} from "../../models/copy-config";
import {AuthenticationService} from "../../../shared/services/login/authentication.service";
import {DELETE_ACCESS} from "../../../shared/model/login/auth.constants";
import {ConfirmationComponent} from "../../../shared/components/confirmation/confirmation.component";
import {FundReport} from "../../models/fund-report";
import {ContextMenuComponent, ContextMenuItem} from "../../../shared/components/context-menu/context-menu.component";
import {MatSort, Sort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {
    TableSettingsDialogComponent,
    TableSettingsDialogData,
    TableSettingsDialogResult
} from "../../../shared/user-setting/table-settings-dialog/table-settings-dialog.component";
import {selectSettingsLoaded, selectTableColumns, selectTableSort} from "../../../shared/user-setting/store/user-setting.selectors";
import {UserSettingActions} from "../../../shared/user-setting/store/user-setting.actions";
import {TableSort} from "../../../shared/generated-sources/api/user-setting";

@Component({
    selector: "valumize-fund-report-new-dialog",
    templateUrl: "./fund-report-new-dialog.component.html",
    styleUrls: ["./fund-report-new-dialog.component.scss"],
    standalone: false
})
export class FundReportNewDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);
    private readonly currentDialog = inject(MatDialogRef<FundReportNewDialogComponent>);
    private readonly authService = inject(AuthenticationService);

    @Input() fundId?: number;
    @Input() currentFundReportId?: number;

    @ViewChild("ctxMenu") contextMenu?: ContextMenuComponent;
    @ViewChild("reportSort") reportSort?: MatSort;

    private fundIsArchived = false;

    subscriptions: Subscription[] = [];

    fundReportsForFund$ = this.store.select(selectSelectedFundReportsForFund);
    isArchived$ = this.store.select(selectFundIsArchived);
    fundReportDeleteError$ = this.store.select(selectFundReportDeleteError);
    // Versioned key: WI-1239's "Last fixes" narrowed the default columns from all 14 to id + Report Date,
    // and anyone who had already opened the dialog carries a persisted 14-column set that would win over the
    // new default (see the `visible.length > 0` fallback in ngOnInit). Bumping the key retires those saved
    // settings so every user sees the new, narrow default; a user who wants the wide layout re-picks it once.
    private static readonly TABLE_KEY = "manage-fund-report-dialog-v2";

    /** User-toggleable columns, in display order (WI-1239). "selected"/"actions" are control columns, always shown. */
    static readonly TOGGLEABLE_COLUMNS = [
        "id", "reportDate",
        "unrealizedAssets", "netCurrentAssets", "gpCarryReserve", "calcNav", "reportedNav",
        "totalDrawdown", "reportedUndrawn", "recallableDistr", "totalUndrawn", "totalDistr",
        "currentTvpi", "carryPaid"
    ];
    /**
     * Shown until the user picks otherwise: just the identifiers (WI-1239 "Last fixes"). The other 12 columns
     * stay available through the table-settings gear. Deliberately NOT all of TOGGLEABLE_COLUMNS — a 14-column
     * table forced the dialog to a fixed 1300px that was mostly empty for the two columns people actually read.
     */
    static readonly DEFAULT_COLUMNS = ["id", "reportDate"];
    static readonly COLUMN_LABELS: Record<string, string> = {
        id: "Fund Report ID",
        reportDate: "Report Date",
        unrealizedAssets: "Unreal. Assets",
        netCurrentAssets: "Net Curr. Assets",
        gpCarryReserve: "GP Carry Reserve",
        calcNav: "Calc. NAV",
        reportedNav: "Rep. NAV",
        totalDrawdown: "Total Drawdown",
        reportedUndrawn: "Rep. Undrawn",
        recallableDistr: "Recall. Distr.",
        totalUndrawn: "Total Undrawn",
        totalDistr: "Total Distr.",
        currentTvpi: "Current TVPI",
        carryPaid: "Carry Paid"
    };
    private static readonly DEFAULT_SORT: TableSort = {field: "reportDate", direction: "asc"};
    /** Every toggleable column is sortable; derived from the labels so the two lists cannot drift (WI-1239). */
    static readonly SORTABLE_COLUMNS = FundReportNewDialogComponent.TOGGLEABLE_COLUMNS.map(column =>
        ({value: column, label: FundReportNewDialogComponent.COLUMN_LABELS[column]}));

    /** Currently selected toggleable columns (persisted); drives the settings dialog + displayed columns. */
    selectedColumns: string[] = [...FundReportNewDialogComponent.DEFAULT_COLUMNS];
    /** Full column list bound by the table (control columns + selected toggleable columns, in order). */
    displayedColumns$ = new BehaviorSubject<string[]>(
        ["selected", ...FundReportNewDialogComponent.DEFAULT_COLUMNS, "actions"]
    );
    dataSource = new MatTableDataSource<FundReport>();

    private applyColumns(tokens: string[]): void {
        const ordered = FundReportNewDialogComponent.TOGGLEABLE_COLUMNS.filter(c => tokens.includes(c));
        this.selectedColumns = ordered;
        this.displayedColumns$.next(["selected", ...ordered, "actions"]);
    }

    /** Current TVPI mirrors the details card: reported-NAV variant when the report is on reported NAV, else calculated (WI-1239). */
    currentTvpi(report: FundReport) {
        return report.tvpiNavType?.code === "REPORTED" ? report.totalTvpiReportedNav : report.totalTVPI;
    }

    /** Column the table is sorted by (WI-1239); persisted via the table settings, default oldest report first. */
    activeSort: TableSort = {...FundReportNewDialogComponent.DEFAULT_SORT};

    onSortChange(sort: Sort) {
        this.activeSort = sort.direction
            ? {field: sort.active, direction: sort.direction}
            : {...FundReportNewDialogComponent.DEFAULT_SORT};
    }

    /**
     * The value a column sorts by. Traceables sort by their payload (date/amount/factor), never by their
     * formatted text, so "10" does not sort before "9". Blank stays `undefined` rather than becoming 0 or ""
     * so that a missing amount is distinguishable from a real zero (WI-1239).
     */
    private sortValue(report: FundReport, column: string): string | number | undefined {
        const value = this.rawSortValue(report, column);
        return value === "" ? undefined : value;
    }

    private rawSortValue(report: FundReport, column: string): string | number | undefined {
        switch (column) {
            case "id":
                return report.id;
            case "reportDate":
                return report.reportDate?.date;
            case "unrealizedAssets":
                return report.totalOfUnrealizedAssets?.amount;
            case "netCurrentAssets":
                return report.netCurrentAssets?.amount;
            case "gpCarryReserve":
                return report.gpCarryReserve?.amount;
            case "calcNav":
                return report.totalNav?.amount;
            case "reportedNav":
                return report.totalReportedNav?.amount;
            case "totalDrawdown":
                return report.totalDrawDowns?.amount;
            case "reportedUndrawn":
                return report.reportedUndrawn?.amount;
            case "recallableDistr":
                return report.recallableDistributions?.amount;
            case "totalUndrawn":
                return report.totalUndrawn?.amount;
            case "totalDistr":
                return report.totalDistributions?.amount;
            case "currentTvpi":
                return this.currentTvpi(report)?.factor;
            case "carryPaid":
                return report.totalCarryPaid?.amount;
            default:
                return undefined;
        }
    }

    /**
     * Sort reports by any column. Reports with no value for that column sort to the end in both
     * directions — a blank is unknown, not the smallest value (WI-1239).
     */
    sortReports(reports: FundReport[], field: string, direction: "asc" | "desc" | ""): FundReport[] {
        if (!field || !direction) {
            return [...reports];
        }
        const dir = direction === "desc" ? -1 : 1;
        return [...reports].sort((a, b) => {
            const valueA = this.sortValue(a, field);
            const valueB = this.sortValue(b, field);
            if (valueA === undefined && valueB === undefined) {
                return 0;
            }
            if (valueA === undefined) {
                return 1;
            }
            if (valueB === undefined) {
                return -1;
            }
            const comparison = typeof valueA === "number" && typeof valueB === "number"
                ? valueA - valueB
                : String(valueA).localeCompare(String(valueB));
            return comparison * dir;
        });
    }

    /**
     * Applies a sort to the table. The column need not be visible: hiding the sorted column only hides
     * its arrow, the row order still reflects the user's chosen sort (WI-1239).
     */
    private applySort(sort: TableSort): void {
        const matSort = this.reportSort;
        if (!matSort) {
            return;
        }
        // Set the state rather than calling sort(): MatSort.sort() toggles the direction when the column
        // is already active, which would flip a sort we are only trying to restore. The explicit emit is
        // what makes MatTableDataSource re-sort.
        matSort.active = sort.field;
        matSort.direction = sort.direction;
        matSort.sortChange.emit({active: sort.field, direction: sort.direction});
    }

    /** Falls back to the default sort when a persisted column no longer exists. */
    private normalizeSort(sort: TableSort): TableSort {
        return FundReportNewDialogComponent.TOGGLEABLE_COLUMNS.includes(sort.field)
            ? {field: sort.field, direction: sort.direction === "desc" ? "desc" : "asc"}
            : {...FundReportNewDialogComponent.DEFAULT_SORT};
    }

    constructor( ) {
    }

    ngOnInit() {
        this.subscriptions.push(this.isArchived$.subscribe(archived => this.fundIsArchived = archived?.value === true));
        this.subscriptions.push(this.fundReportsForFund$.subscribe(reports => this.dataSource.data = reports));

        // Column + sort settings (WI-1239): apply the user's persisted table settings once loaded,
        // falling back to the defaults (all columns, ascending by Report Date).
        this.subscriptions.push(
            this.store.select(selectSettingsLoaded).pipe(filter(Boolean), take(1)).subscribe(() => {
                this.store.select(selectTableColumns(FundReportNewDialogComponent.TABLE_KEY)).pipe(take(1))
                    .subscribe(savedColumns => {
                        const visible = (savedColumns ?? []).filter(c => FundReportNewDialogComponent.TOGGLEABLE_COLUMNS.includes(c));
                        this.applyColumns(visible.length > 0 ? visible : FundReportNewDialogComponent.DEFAULT_COLUMNS);
                    });
                this.store.select(selectTableSort(FundReportNewDialogComponent.TABLE_KEY)).pipe(take(1))
                    .subscribe(savedSort => {
                        if (savedSort) {
                            this.activeSort = this.normalizeSort(savedSort);
                            this.applySort(this.activeSort);
                        }
                    });
            })
        );
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.reportSort ?? null;
        // MatTableDataSource's own comparator cannot express "blanks last", so sorting goes through
        // sortReports; MatSort is still what tracks the active column and renders the header arrow.
        this.dataSource.sortData = (data, sort) => this.sortReports(data, sort.active, sort.direction);
        // Deferred: settings may have been applied during ngOnInit, before MatSort existed.
        Promise.resolve().then(() => this.applySort(this.activeSort));
    }

    openAddReportDialog(): void {
        if (!this.fundId) {
            return;
        }
        // Opened on top of this dialog, which stays open so the user can keep managing reports (WI-1239).
        // The Add dialog reads the fund's existing report dates from the store itself, so it stays correct
        // even if the reports are still loading when it opens.
        this.dialog.open(AddFundReportDialogComponent, {data: {fundId: this.fundId}});
    }

    navigateToCopyWorkflowStepper(fundReport: FundReport) {
        const copyConfig = this.createCopyConfig(fundReport);
        if (!copyConfig) {
            return;
        }

        const dialogRef = this.dialog.open(FundCopyWorkflowStepperComponent, {disableClose: true});
        dialogRef.componentInstance.startWith = "report";
        dialogRef.componentInstance.fundId = this.fundId;

        this.currentDialog.close();
    }

    private createCopyConfig(fundReport: FundReport) {
        const fundReportId = fundReport.id;
        if (!fundReportId) {
            return;
        }

        // The new report date is chosen in step 1 of the copy workflow; seed a today placeholder so that
        // step's picker defaults to the latest past quarter-end rather than a fixed date (WI-1239).
        const fundReportCopyConfig: FundReportCopyConfig = {
            fundReportId,
            reportDate: DateUtil.isoDate(),
            fundInvestmentsCopyConfig: []
        }
        const copyConfig: CopyConfig = {fundReportCopyConfig};

        this.store.dispatch(CopyFundReportAndValuationsActions.updateCopyConfig({copyConfig}));
        return copyConfig;
    }

    get showDeleteButton(): boolean {
        return this.authService.currentUser?.hasAnyPermission(DELETE_ACCESS) ?? false;
    }

    onRowContextMenu(event: MouseEvent, report: FundReport): void {
        const items: ContextMenuItem[] = [
            {
                label: "Delete Fund Report",
                icon: "delete",
                hidden: !this.showDeleteButton,
                action: () => this.openDeleteConfirmation(report.id)
            }
        ];
        this.contextMenu?.open(event, items);
    }

    openDeleteConfirmation(fundReportId?: number): void {
        if (fundReportId === undefined) {
            return;
        }
        const dialogRef = this.dialog.open(ConfirmationComponent);

        dialogRef.componentInstance.confirmMessage = "Delete Fund Report";
        dialogRef.componentInstance.htmlContent = `
        <p>Are you sure you want to delete this fund report?</p>
        <p>Associated fund investments will also be deleted.</p>
        <p>You can only delete a fund report if no fund valuations are associated with it.</p>
        <p><strong>This action cannot be undone.</strong></p>`;

        dialogRef.afterClosed().pipe(
            map(result => {
                if (result && this.fundId !== undefined) {
                    this.store.dispatch(FundReportActions.delete({fundId: this.fundId, fundReportId: fundReportId}));
                }
            })
        ).subscribe();
    }

    openTableSettings(): void {
        const dialogData: TableSettingsDialogData = {
            tableKey: FundReportNewDialogComponent.TABLE_KEY,
            columnGroups: [{label: "Columns", cols: FundReportNewDialogComponent.TOGGLEABLE_COLUMNS}],
            columnLabels: FundReportNewDialogComponent.COLUMN_LABELS,
            currentColumns: this.selectedColumns,
            currentPageSize: 0,
            currentSort: this.activeSort,
            pageSizeOptions: [],
            sortableColumns: FundReportNewDialogComponent.SORTABLE_COLUMNS,
            isAdmin: false,
            hidePageSize: true,
            defaultColumns: [...FundReportNewDialogComponent.DEFAULT_COLUMNS],
            defaultPageSize: 0,
            defaultSort: FundReportNewDialogComponent.DEFAULT_SORT
        };

        this.dialog.open(TableSettingsDialogComponent, {data: dialogData, width: "520px"})
            .afterClosed()
            .subscribe((result: TableSettingsDialogResult) => this.handleDialogResult(result));
    }

    private handleDialogResult(result: TableSettingsDialogResult): void {
        if (!result) {
            return;
        }
        this.applyColumns(result.columns);
        this.activeSort = this.normalizeSort(result.sort);
        this.applySort(this.activeSort);

        const payload = {
            tableKey: FundReportNewDialogComponent.TABLE_KEY,
            columns: result.columns,
            pageSize: result.pageSize,
            sort: result.sort
        };
        if (result.saveAdmin) {
            this.store.dispatch(UserSettingActions.saveAdminTableSettings(payload));
        }
        if (result.saveUser) {
            this.store.dispatch(UserSettingActions.saveTableSettings(payload));
        }
        if (!result.saveUser && !result.saveAdmin) {
            this.store.dispatch(UserSettingActions.applyTableSettings(payload));
        }
    }

    ngOnDestroy() {
        this.store.dispatch(FundReportActions.clearDeleteError());
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
